import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.guide-dropdown')) {
        setGuideOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const navTextClass = () =>
    `text-sm font-medium transition-colors hover:text-primary-500 ${scrolled ? 'text-foreground-700' : 'text-white/90'}`;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-[4.5rem]">
          <Link to="/" className="flex items-center gap-2 group flex-shrink-0">
            <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-lg bg-primary-500">
              <i className="ri-file-excel-2-line text-lg text-white"></i>
            </div>
            <span className={`font-heading text-lg md:text-xl transition-colors duration-300 ${
              scrolled ? 'text-foreground-900' : 'text-white'
            }`}>
              CSS Spreadsheet
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-4 lg:gap-6">
            <Link to="/" className={navTextClass()}>Home</Link>
            <a href="#categories" className={navTextClass()}>Categories</a>
            <a href="#about" className={navTextClass()}>About</a>

            <div className="relative guide-dropdown">
              <button
                onClick={() => setGuideOpen(!guideOpen)}
                className={`flex items-center gap-1 ${navTextClass()}`}
              >
                <span>Shopping Guide</span>
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${guideOpen ? 'rotate-180' : ''}`}></i>
                </div>
              </button>
              {guideOpen && (
                <div className="absolute top-full right-0 mt-2 w-72 bg-background-50 border border-background-200/70 rounded-xl overflow-hidden animate-scale-in z-40">
                  <div className="py-2">
                    <Link
                      to="/article/cssbuy-spreadsheet-complete-guide"
                      onClick={() => setGuideOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 transition-colors"
                    >
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-guide-line text-base"></i>
                      </div>
                      <div>
                        <p className="font-semibold text-foreground-800">CSSBuy Spreadsheet Guide</p>
                        <p className="text-xs text-foreground-500">Complete 2026 shopping tutorial</p>
                      </div>
                    </Link>
                    <Link
                      to="/guides"
                      onClick={() => setGuideOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 transition-colors"
                    >
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                        <i className="ri-book-open-line text-base"></i>
                      </div>
                      <div>
                        <p className="font-semibold text-foreground-800">All Guides</p>
                        <p className="text-xs text-foreground-500">Browse all shopping articles</p>
                      </div>
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-semibold whitespace-nowrap px-5 py-2.5 rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-all duration-200"
            >
              CSSBuy Sheet
            </a>
          </div>

          <button
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg bg-background-100/60"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-lg ${scrolled ? 'text-foreground-800' : 'text-white'} ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden bg-background-50 rounded-xl border border-background-200/60 mt-2 py-3 px-3 flex flex-col gap-1 animate-scale-in">
            <Link to="/" className="flex items-center gap-3 text-sm font-medium text-foreground-700 py-2.5 px-3 rounded-lg hover:bg-background-100" onClick={() => setMobileOpen(false)}>
              <div className="w-7 h-7 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 flex-shrink-0">
                <i className="ri-home-line text-sm"></i>
              </div>
              Home
            </Link>
            <a href="#categories" className="flex items-center gap-3 text-sm font-medium text-foreground-700 py-2.5 px-3 rounded-lg hover:bg-background-100" onClick={() => setMobileOpen(false)}>
              <div className="w-7 h-7 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-600 flex-shrink-0">
                <i className="ri-grid-line text-sm"></i>
              </div>
              Categories
            </a>
            <a href="#about" className="flex items-center gap-3 text-sm font-medium text-foreground-700 py-2.5 px-3 rounded-lg hover:bg-background-100" onClick={() => setMobileOpen(false)}>
              <div className="w-7 h-7 flex items-center justify-center rounded-md bg-accent-100 text-accent-600 flex-shrink-0">
                <i className="ri-information-line text-sm"></i>
              </div>
              About
            </a>
            <Link to="/article/cssbuy-spreadsheet-complete-guide" className="flex items-center gap-3 text-sm font-medium text-foreground-700 py-2.5 px-3 rounded-lg hover:bg-background-100" onClick={() => setMobileOpen(false)}>
              <div className="w-7 h-7 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 flex-shrink-0">
                <i className="ri-guide-line text-sm"></i>
              </div>
              CSSBuy Guide
            </Link>
            <Link to="/guides" className="flex items-center gap-3 text-sm font-medium text-foreground-700 py-2.5 px-3 rounded-lg hover:bg-background-100" onClick={() => setMobileOpen(false)}>
              <div className="w-7 h-7 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-600 flex-shrink-0">
                <i className="ri-book-open-line text-sm"></i>
              </div>
              All Guides
            </Link>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm font-semibold whitespace-nowrap py-2.5 px-5 rounded-full bg-primary-500 text-white text-center mt-1" onClick={() => setMobileOpen(false)}>
              CSSBuy Sheet
            </a>
          </div>
        )}
      </div>
    </nav>
  );
}